import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';

function useTargetRect(testId, active) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!testId) {
      setRect(null);
      return;
    }
    const el = document.querySelector(`[data-testid="${testId}"]`);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [testId]);

  useEffect(() => {
    if (!active) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, measure]);

  return rect;
}

export default function DashboardTour({ tour }) {
  const { active, currentStepData, currentStep, totalSteps, next, back, skip } = tour;
  const rect = useTargetRect(currentStepData?.targetTestId, active);

  // Target not found (e.g. testid missing/renamed) — skip this step rather
  // than showing a spotlight pointed at nothing.
  useEffect(() => {
    if (active && currentStepData && rect === null) {
      const el = document.querySelector(`[data-testid="${currentStepData.targetTestId}"]`);
      if (!el) next();
    }
  }, [active, currentStepData, rect, next]);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') skip();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, skip]);

  if (!active || !currentStepData || !rect) return null;

  const PADDING = 8;
  const blockerStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 99,
  };
  const spotlightStyle = {
    position: 'fixed',
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
    borderRadius: '12px',
    boxShadow: '0 0 0 9999px rgba(29, 58, 46, 0.65)',
    pointerEvents: 'none',
    zIndex: 100,
    transition: 'all 0.2s ease',
  };

  const calloutTop = rect.bottom + PADDING + 12;
  const calloutBelowFits = calloutTop + 180 < window.innerHeight;
  const calloutStyle = {
    position: 'fixed',
    left: Math.max(16, Math.min(rect.left, window.innerWidth - 336)),
    top: calloutBelowFits ? calloutTop : rect.top - PADDING - 12,
    transform: calloutBelowFits ? 'none' : 'translateY(-100%)',
    zIndex: 101,
    width: 320,
  };

  const isLast = currentStep === totalSteps - 1;

  return (
    <>
      <div style={blockerStyle} data-testid="dashboard-tour-blocker" />
      <div style={spotlightStyle} data-testid="dashboard-tour-spotlight" />
      <div
        style={calloutStyle}
        className="bg-white rounded-2xl shadow-2xl p-5 border border-sand-darker"
        data-testid="dashboard-tour-callout"
        role="dialog"
        aria-modal="true"
      >
        <div aria-live="polite">
          <div className="text-xs font-semibold text-moana uppercase tracking-wide mb-2">
            {currentStep + 1} of {totalSteps}
          </div>
          <h3 className="font-heading text-lg font-semibold text-fern-dark mb-1.5">{currentStepData.title}</h3>
          <p className="text-sm text-slate-600 mb-4">{currentStepData.body}</p>
        </div>
        <div className="flex items-center justify-between">
          <button
            onClick={skip}
            className="text-xs text-slate-400 hover:text-slate-600"
            data-testid="dashboard-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" size="sm" onClick={back} data-testid="dashboard-tour-back">
                Back
              </Button>
            )}
            <Button size="sm" className="bg-fern hover:bg-fern-dark text-white" onClick={next} data-testid="dashboard-tour-next">
              {isLast ? "Got it!" : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
